import { type InfiniteData, infiniteQueryOptions, useInfiniteQuery } from '@tanstack/react-query';
import { nextCursorParam } from '@/hooks/queries/pagination';
import { useAutoFetchNextPage } from '@/hooks/use-auto-fetch-next-page';
import { type ClientCategorisation, type ClientCategorisationPage, getClientCategorisationPage, type UserClientCategorisation } from '@/services/api/client-categorisation';

export interface ClientCategorisationData {
  rows: UserClientCategorisation[];
  /**
   * The most recent page carried 0 rows. Routine under a categorisation filter:
   * DynamoDB applies `Limit` BEFORE `FilterExpression`, so a filtered
   * page can be empty while a cursor remains — drives the auto-advance below.
   */
  isLastPageEmpty: boolean;
}

// Stable module-level select — an inline closure recreated per render defeats
// TanStack Query's select memoization and re-flattens the full accumulated
// dataset on every render.
// `?? []` guards malformed/empty payloads — an undefined page.data would smuggle
// `undefined` rows into the table and crash the render into the root error boundary
const selectRows = (data: InfiniteData<ClientCategorisationPage>): ClientCategorisationData => ({
  rows: data.pages.flatMap((page) => page.data ?? []),
  isLastPageEmpty: (data.pages.at(-1)?.data?.length ?? 0) === 0,
});

export const CLIENT_CATEGORISATION_QUERY_KEY = ['users', 'futures', 'client-categorisation'] as const;

export const clientCategorisationInfiniteQueryOptions = (categorisation?: ClientCategorisation) =>
  infiniteQueryOptions({
    // A filter change rotates the key — the legacy composable's
    // reset-on-filter-change (cursor = undefined, list = []) for free
    queryKey: [...CLIENT_CATEGORISATION_QUERY_KEY, categorisation ?? 'all'],
    queryFn: ({ pageParam }) => getClientCategorisationPage(pageParam, categorisation),
    initialPageParam: undefined as string | undefined,
    // Deliberate fix: CURSOR-ONLY continuation. Legacy stopped on any
    // empty page even when a cursor remained (`done(data.length === 0 || !cursor
    // ? 'empty' : 'ok')`), silently truncating filtered lists.
    getNextPageParam: nextCursorParam,
    select: selectRows,
  });

/**
 * Infinite LEK-paginated client-categorisation list, optionally server-filtered
 * by categorisation. Auto-advances through empty filtered pages.
 */
export const useClientCategorisation = (categorisation?: ClientCategorisation) => {
  const query = useInfiniteQuery(clientCategorisationInfiniteQueryOptions(categorisation));
  const isLastPageEmpty = query.data?.isLastPageEmpty ?? false;

  // Deliberate fix: auto-advance — when a fetched page contributed 0
  // rows but a cursor exists, fetch the next page immediately so sparse filtered
  // pages stream through without the user scrubbing the sentinel.
  // NOTE: bop-api still DISCARDS the LEK when a page enriches to 0 rows,
  // so a filtered list may end early on a fully-filtered-out page —
  // upstream bug, to be filed against bop-api; nothing more the client can do.
  useAutoFetchNextPage(query, isLastPageEmpty);

  return query;
};
